import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { statusLabel, statusTone } from '../lib/format';

export function Spinner({ label = 'Loading' }: { label?: string }) {
  return (
    <span className="row row--tight" role="status">
      <span className="spinner" aria-hidden="true" />
      <span className="sr-only">{label}</span>
    </span>
  );
}

export function Loading({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="loading-block" role="status">
      <span className="spinner" aria-hidden="true" />
      <span>{label}</span>
    </div>
  );
}

export function Banner({
  tone = 'info', title, children, icon,
}: {
  tone?: 'info' | 'ok' | 'warn' | 'danger' | 'brand';
  title?: string;
  children?: ReactNode;
  icon?: string;
}) {
  const defaults = { info: 'ℹ', ok: '✓', warn: '!', danger: '⚠', brand: '◆' };
  return (
    <div className={`banner banner--${tone}`} role={tone === 'danger' ? 'alert' : undefined}>
      <span className="banner__icon" aria-hidden="true">{icon ?? defaults[tone]}</span>
      <div style={{ minWidth: 0 }}>
        {title && <strong className="banner__title">{title}</strong>}
        {children}
      </div>
    </div>
  );
}

export function StatusPill({ status }: { status: string }) {
  const tone = statusTone(status);
  return (
    <span className={`pill${tone ? ` pill--${tone}` : ''}`}>
      <span className="pill__dot" aria-hidden="true" />
      {statusLabel(status)}
    </span>
  );
}

export function Field({
  label, hint, error, children, id, required,
}: {
  label: string; hint?: string; error?: string | null;
  children: ReactNode; id?: string; required?: boolean;
}) {
  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {label}{required && <span aria-hidden="true" style={{ color: 'var(--danger)' }}> *</span>}
      </label>
      {children}
      {hint && !error && <span className="field__hint">{hint}</span>}
      {error && <span className="field__error" role="alert">{error}</span>}
    </div>
  );
}

export function Modal({
  title, children, onClose, actions, labelledBy = 'modal-title',
}: {
  title: string; children: ReactNode; onClose: () => void;
  actions?: ReactNode; labelledBy?: string;
}) {
  const panel = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    panel.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
      if (event.key !== 'Tab' || !panel.current) return;
      // Keep focus inside the dialog while it is open.
      const focusable = panel.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault(); first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
      previous?.focus?.();
    };
  }, [onClose]);

  return (
    <div className="modal-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal" role="dialog" aria-modal="true" aria-labelledby={labelledBy}
           ref={panel} tabIndex={-1}>
        <h2 className="modal__title" id={labelledBy}>{title}</h2>
        {children}
        {actions && <div className="modal__actions">{actions}</div>}
      </div>
    </div>
  );
}

export function EmptyState({ title, children, action }: {
  title: string; children?: ReactNode; action?: ReactNode;
}) {
  return (
    <div className="empty stack">
      <h3 style={{ margin: 0, color: 'var(--ink-2)' }}>{title}</h3>
      {children && <p className="small" style={{ maxWidth: '46ch', margin: '0 auto' }}>{children}</p>}
      {action}
    </div>
  );
}

export function Meter({ used, allowance }: { used: number; allowance: number }) {
  const ratio = allowance > 0 ? Math.min(used / allowance, 1) : 0;
  const tone = ratio >= 1 ? 'danger' : ratio >= 0.8 ? 'warn' : '';
  return (
    <div className="meter" role="meter" aria-valuenow={used} aria-valuemin={0}
         aria-valuemax={allowance}
         aria-label={`${used} of ${allowance} signature requests used`}>
      <div className={`meter__fill${tone ? ` meter__fill--${tone}` : ''}`}
           style={{ width: `${ratio * 100}%` }} />
    </div>
  );
}

/**
 * Assurance disclosure. Rendered wherever a final document or its evidence is
 * presented, so no surface can imply a seal that was never applied.
 */
export function AssuranceNotice({ profile, compact }: { profile?: string | null; compact?: boolean }) {
  const sealed = profile === 'phase2_sectigo_usb';
  if (compact) {
    return (
      <span className={`pill ${sealed ? 'pill--brand' : ''}`}>
        {sealed ? 'Organization seal applied' : 'Evidence-only — no PDF seal'}
      </span>
    );
  }
  if (sealed) {
    return (
      <Banner tone="brand" title="Organization-level platform seal">
        This document carries a platform seal identifying the sealing organization
        and an independent trusted timestamp. It is not a qualified electronic seal
        or signature, and it does not establish that a particular individual
        independently controlled the signing key.
      </Banner>
    );
  }
  return (
    /* Same facts as before, led with what the evidence *is* rather than a list
     * of cryptography that is absent.
     *
     * The absence still has to be stated -- C09 forbids implying certificate
     * trust we do not have, and the verification report and export must report
     * seal, certificate and timestamp as "not applied" (F14, H07). Those are
     * the surfaces the requirement binds, and they are unchanged. What is not
     * required is opening with three negatives at a signer who wants to know
     * their signature landed. */
    <Banner tone="info" title="How this document is protected">
      Its exact bytes are fingerprinted with SHA-256, every action is recorded
      in an append-only event log, and the evidence package is signed so any
      later change is detectable. You can verify a copy yourself, without us.
      {' '}
      <span className="muted">
        This is evidence-based integrity rather than a certificate-based PDF
        seal: no PDF seal, external certificate or third-party timestamp is
        applied.
      </span>
    </Banner>
  );
}
